import React, { useState, useEffect } from "react";
import { message, Form, Button, Select, InputNumber, DatePicker } from "antd";
import dayjs from "dayjs";
import { TbLicense } from "react-icons/tb";
import MegaphoneModal from "../MegaphoneModal/MegaphoneModal";
import styles from "./AssignTrialModal.module.scss";
import UserManagementFunction from "../../Functions/UserManagement/UserManagement";
import { useSelector } from "react-redux";
import { RootState } from "../../Redux/store";

interface AssignTrialModalProps {
  visible: boolean;
  onCancel: () => void;
  onTrialAssigned: () => void;
}

const AssignTrialModal: React.FC<AssignTrialModalProps> = ({
  visible,
  onCancel,
  onTrialAssigned,
}) => {
  const [trialOptions, setTrialOptions] = useState<
    { id: string; description: string }[]
  >([]);
  const typeOptions = [
    {
      label: "Trial",
      value: "TRIAL",
    },
    {
      label: "Perpetual",
      value: "PERPETUAL",
    },
  ];
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [maxLicenses, setMaxLicenses] = useState(10);
  const user = useSelector((state: RootState) => state.user);
  const { custID } = user.user!!;
  const accessToken = useSelector(
    (state: RootState) => state.access.accessToken
  );
  const sessionToken = useSelector((state: RootState) => state.user.token);
  useEffect(() => {
    const fetchTrialOptions = async () => {
      try {
        const response = await UserManagementFunction.getLicenseTypes(
          accessToken!!,
          sessionToken!!
        );
        if (response.statusCode === 200) {
          setTrialOptions(response.data);
        }
      } catch (error) {
        message.error("Failed to fetch trial options!");
      }
    };
    fetchTrialOptions();
  }, []);

  const handleSubmit = () => {
    setSubmitting(true);
    form
      .validateFields()
      .then((values) => {
        const assignDate = dayjs();
        const expireDate = dayjs(values.lastOffDate);

        if (expireDate.isBefore(assignDate, "day")) {
          message.error("Expire date must be in the future!");
          setSubmitting(false);
          return;
        }

        const trialLicense = {
          custId: custID!,
          licenseId: values.selectLicense,
          value: values.numberOfLicenses.toString(),
          usedValue: "0",
          userCreated: user.user?.id!!.toString() ?? "",
          assignDate: dayjs().toISOString(),
          lastOffDate: values.lastOffDate,
          isActive: true,
          type: values.type,
          cgsStatus: "Active",
          userLicenses: [],
        };

        UserManagementFunction.createNewTrialLicense(
          accessToken!!,
          sessionToken!!,
          trialLicense
        )
          .then((response) => {
            if (response.statusCode === 200) {
              message.success("Trial assigned successfully!");
              form.resetFields();
              onTrialAssigned();
              onCancel();
            } else {
              message.error("Failed to assign license!");
            }
          })
          .catch((error) => {
            message.error("Failed to assign license!");
            console.log(error);
          })
          .finally(() => {
            setSubmitting(false);
          });
      })
      .catch((error) => {
        console.log(error);
        setSubmitting(false);
      });
  };

  return (
    <MegaphoneModal
      modalKey="assign_trial_modal"
      title="Assign License"
      icon={TbLicense}
      width={500}
      visible={visible}
      onClose={onCancel}
      closable={true}
      footer={[
        <Button key="back" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={submitting}
        >
          Assign
        </Button>,
      ]}
    >
      <div className={styles.formBody}>
        <Form
          layout="horizontal"
          form={form}
          name="assign_trial_form"
          initialValues={{
            numberOfLicenses: 1,
            lastOffDate: dayjs().add(30, "day"),
          }}
        >
          <Form.Item
            name="selectLicense"
            rules={[{ required: true, message: "Please select a license!" }]}
          >
            <Select
              showSearch
              style={{ width: "100%" }}
              options={trialOptions.map((trial) => ({
                value: trial.id,
                label: trial.description,
              }))}
              placeholder="Select a license"
              optionFilterProp="children"
              onSelect={(value) => {
                const selectedLicense = trialOptions.find(
                  (trial) => trial.id === value
                );
                if (
                  selectedLicense?.description
                    .toLowerCase()
                    .includes("virtual office")
                ) {
                  setMaxLicenses(50);
                } else {
                  setMaxLicenses(10);
                }
              }}
            ></Select>
          </Form.Item>

          <Form.Item
            name="type"
            rules={[
              { required: true, message: "Please select a type of license!" },
            ]}
          >
            <Select
              showSearch
              style={{ width: "100%" }}
              options={typeOptions}
              placeholder="Select a type of license"
              optionFilterProp="children"
              onSelect={(selected) => {
                if (selected && selected === "TRIAL") {
                  setIsDisabled(false);
                } else {
                  setIsDisabled(true);
                }
              }}
            ></Select>
          </Form.Item>

          <Form.Item
            name="numberOfLicenses"
            label="Number of Licenses"
            rules={[
              {
                required: true,
                message: "Please input the number of licenses!",
              },
            ]}
          >
            <InputNumber min={1} max={maxLicenses} />
          </Form.Item>

          <Form.Item
            name="lastOffDate"
            label="Expire Date"
            rules={[
              { required: true, message: "Please select the expire date!" },
            ]}
          >
            <DatePicker
              style={{ width: "200px" }}
              minDate={dayjs()}
              maxDate={dayjs().add(4, "month")}
              format={"MM/DD/YYYY"}
              disabled={isDisabled}
            />
          </Form.Item>
        </Form>
      </div>
    </MegaphoneModal>
  );
};

export default AssignTrialModal;
